import Papa, { type ParseError, type ParseResult } from "papaparse";

// Same caps as the schema-side parser. Sample shipped to the LLM is bounded;
// the *full* file object is retained so the upload-job dispatch can use it.
export const CSV_MAX_FILE_BYTES = 10 * 1024 * 1024;
export const CSV_MAX_SAMPLED_ROWS = 10;
export const CSV_MAX_SAMPLED_COLS = 100;

export interface ParsedDataObjectsCsv {
    file: File;                  // kept so the bulk-upload dispatch can re-use it
    fileName: string;
    fileSizeBytes: number;
    rowCount: number;            // total rows in the file (not yet decided as data/header)
    columnCount: number;         // total columns in the file
    sampledRowCount: number;
    sampledColumnCount: number;
    sampleRows: Array<Array<string | null>>;  // array-of-arrays — header detection deferred to the LLM
    truncated: { rows: boolean; columns: boolean };
    parseErrors: string[];
}

function coerceCell(value: unknown): string | null {
    if (value === null || value === undefined) return null;
    const s = String(value).trim();
    return s === "" ? null : s;
}

export async function parseDataObjectsCsv(file: File): Promise<ParsedDataObjectsCsv> {
    if (file.size > CSV_MAX_FILE_BYTES) {
        throw new Error(`File is larger than ${(CSV_MAX_FILE_BYTES / 1024 / 1024).toFixed(0)} MB.`);
    }
    return new Promise<ParsedDataObjectsCsv>((resolve, reject) => {
        Papa.parse<string[]>(file, {
            header: false,
            skipEmptyLines: "greedy",
            complete: (result: ParseResult<string[]>) => {
                try {
                    const allRows = (result.data || []) as string[][];
                    const rowCount = allRows.length;
                    const columnCount = allRows.reduce((max, r) => Math.max(max, (r || []).length), 0);
                    const sampledRowCount = Math.min(rowCount, CSV_MAX_SAMPLED_ROWS);
                    const sampledColumnCount = Math.min(columnCount, CSV_MAX_SAMPLED_COLS);
                    const sampleRows: Array<Array<string | null>> = [];
                    for (let i = 0; i < sampledRowCount; i++) {
                        const row = allRows[i] || [];
                        const trimmed: Array<string | null> = [];
                        for (let c = 0; c < sampledColumnCount; c++) {
                            trimmed.push(coerceCell(row[c]));
                        }
                        sampleRows.push(trimmed);
                    }
                    resolve({
                        file,
                        fileName: file.name,
                        fileSizeBytes: file.size,
                        rowCount,
                        columnCount,
                        sampledRowCount,
                        sampledColumnCount,
                        sampleRows,
                        truncated: {
                            rows: rowCount > CSV_MAX_SAMPLED_ROWS,
                            columns: columnCount > CSV_MAX_SAMPLED_COLS,
                        },
                        parseErrors: (result.errors || []).map((e: ParseError) => e.message),
                    });
                } catch (e: any) {
                    reject(e);
                }
            },
            error: (err: Error) => reject(err),
        });
    });
}

// For headerless CSVs we prepend a synthetic header row built from the LLM's
// column → field mapping so the existing bulk-upload backend (which expects
// row[0] = column names) keeps working untouched. The caller supplies the
// header names — for columns that the LLM mapped to a schema field, the
// header is the schema field name; unmapped columns fall back to "Column N".
export function syntheticColumnName(index0: number): string {
    return `Column ${index0 + 1}`;
}

function csvEscape(value: string): string {
    return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

// Reads the original file, prefixes a header row built from the supplied
// names, and returns a new File ready to upload. Preserves the original
// mime / name.
export async function prependHeaderRow(file: File, headerNames: string[]): Promise<File> {
    const headerRow = headerNames.map(csvEscape).join(",");
    const text = await file.text();
    const linebreak = text.includes("\r\n") ? "\r\n" : "\n";
    const newBlob = new Blob([headerRow, linebreak, text], { type: file.type || "text/csv" });
    return new File([newBlob], file.name, { type: file.type || "text/csv" });
}
